import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Heart, Gift, Music, Sparkles, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const AnniversaryGiftPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [hearts, setHearts] = useState([]);

  // Generate floating hearts
  useEffect(() => {
    const newHearts = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 10 + Math.random() * 10,
      size: 30 + Math.random() * 40,
      color: ['text-pink-300', 'text-red-400', 'text-rose-400', 'text-pink-200'][Math.floor(Math.random() * 4)]
    }));
    setHearts(newHearts);
  }, []);

  const handleGiftClick = async () => {
    if (isOpen) return;
    setIsOpen(true);

    // Enhanced confetti
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random(), y: Math.random() * 0.5 },
        colors: ['#ff69b4', '#ff1493', '#ffd700', '#ff4500', '#ff9a9e', '#ff6b6b', '#ffd93d'],
        shapes: ['heart', 'circle'],
      });
    }, 250);

    // Show message with delay
    setTimeout(() => setShowMessage(true), 1200);

    // Play audio
    if (audioEnabled) {
      try {
        const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-magical-sparkle-chime-581.mp3');
        audio.volume = 0.3;
        await audio.play();
      } catch (e) {
        console.log('Audio blocked:', e);
      }
    }
  };

  const closeMessage = () => {
    setShowMessage(false);
    setTimeout(() => setIsOpen(false), 600);
  };

  return (
    <>
      {/* Background with gradient overlay */}
      <div className="fixed inset-0 bg-gradient-to-br from-pink-100 via-rose-50 to-red-50" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pink-200/20 via-transparent to-transparent" />


      {/* Floating Hearts with custom float animation */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
        {hearts.map((heart) => (
          <div
            key={heart.id}
            className={`absolute ${heart.color} animate-float`}
            style={{
              left: `${heart.left}%`,
              fontSize: `${heart.size}px`,
              animationDelay: `${heart.delay}s`,
              animationDuration: `${heart.duration}s`,
            }}
          >
            <Heart fill="currentColor" />
          </div>
        ))}
      </div>

      <div className="relative min-h-screen flex flex-col items-center justify-center p-4 z-20">
        {/* Gift Box with 3D perspective */}
        <div className="perspective-1000 mb-8">
          <div
            className={`relative w-72 h-72 md:w-96 md:h-96 cursor-pointer select-none transition-all duration-700 ${
              isOpen ? 'scale-110' : 'hover:scale-105'
            }`}
            onClick={handleGiftClick}
          >
            {/* Lid with 3D flip */}
            <div
              className={`absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-red-600 to-red-700 rounded-t-2xl shadow-2xl z-20 flex items-center justify-center transition-transform duration-1000 ease-out ${
                isOpen ? 'rotate-3d-open' : ''
              }`}
              style={{
                transformOrigin: 'top center',
                transformStyle: 'preserve-3d',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
            >
              <div className="w-24 h-5 bg-red-800 rounded-full shadow-inner"></div>
            </div>

            {/* Box Body */}
            <div
              className={`absolute bottom-0 left-0 w-full h-60 md:h-72 bg-gradient-to-b from-red-500 to-red-600 rounded-b-2xl shadow-2xl z-10 overflow-hidden transition-all duration-700 ${
                isOpen ? 'translate-y-10' : ''
              }`}
            >
              {/* Inner glow when opened */}
              {isOpen && (
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-200/20 to-transparent animate-pulse" />
              )}
              <div className={`w-full h-full flex items-center justify-center transition-opacity duration-500 ${isOpen ? 'opacity-0' : 'opacity-100'}`}>
                <Gift className="w-32 h-32 text-white drop-shadow-lg animate-pulse" />
              </div>
              
              {/* Surprise inside */}
              {isOpen && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <Heart className="w-24 h-24 text-red-500 fill-red-500 animate-heartbeat drop-shadow-xl" />
                    <Sparkles className="absolute -top-4 -left-4 w-8 h-8 text-yellow-400 animate-pulse" />
                    <Sparkles className="absolute -bottom-4 -right-4 w-8 h-8 text-yellow-400 animate-pulse delay-200" />
                  </div>
                </div>
              )}
            </div>

            {/* Ribbon Vertical */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-full bg-gradient-to-b from-yellow-400 to-yellow-500 z-30 shadow-md"></div>
            {/* Ribbon Horizontal */}
            <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-full h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 z-30 shadow-md"></div>

            {/* Bow with layered effect */}
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 w-32 h-32 z-40">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full shadow-xl"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-br from-yellow-300 to-yellow-400 rounded-full shadow-lg"></div>
              {/* Bow loops */}
              <div className="absolute -left-6 top-2 w-12 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transform rotate-45 shadow-md"></div>
              <div className="absolute -right-6 top-2 w-12 h-16 bg-gradient-to-l from-yellow-400 to-yellow-500 rounded-full transform -rotate-45 shadow-md"></div>
            </div>
          </div>
        </div>

        {/* Click Prompt */}
        {!isOpen && (
          <div className="mt-8 text-center">
            <p className="text-2xl md:text-3xl font-medium text-red-600 animate-pulse-soft font-dancing">
              Click to open your gift! 
            </p>
            <p className="text-sm text-red-400 mt-2">✨</p>
          </div>
        )}
      </div>

      {/* Surprise Message Modal with backdrop */}
      {showMessage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-fade-in"
          onClick={closeMessage}
        >
          <div
            className="relative bg-white/95 rounded-3xl shadow-2xl p-8 md:p-12 max-w-2xl w-full transform transition-all duration-700 scale-100 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative corners */}
            <div className="absolute top-4 left-4 text-pink-300/50">
              <Sparkles className="w-8 h-8 animate-pulse" />
            </div>
            <div className="absolute bottom-4 right-4 text-pink-300/50">
              <Sparkles className="w-8 h-8 animate-pulse delay-300" />
            </div>
            
            <div className="flex justify-center mb-6">
              <Heart className="w-20 h-20 text-red-500 fill-red-500 animate-heartbeat drop-shadow-xl" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-pink-600 mb-6 font-dancing">
              Happy Anniversary, My Love! 💕
            </h1>
            
            <div className="space-y-4 mb-8">
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
                Every moment with you is a treasure.
              </p>
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
                You've filled my life with love, laughter, and endless joy.
              </p>
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-semibold text-pink-600">
                Unaku na tha periya gift enaiya nalla pathukura valiya paru. 🌹
              </p>
            </div>
            
            <p className="text-sm text-gray-500 italic mb-8">
              ~ With all my heart, forever yours
            </p>
            
            <button
              onClick={closeMessage}
              className="group relative px-10 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-lg rounded-full hover:from-pink-600 hover:to-rose-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
            >
              <span className="relative z-10">Close with Love</span>
              <div className="absolute inset-0 bg-white/20 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
            </button>
          </div>
        </div>
      )}


      {/* Global Styles */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap');
        
        .font-dancing {
          font-family: 'Dancing Script', cursive;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .rotate-3d-open {
          transform: rotateX(-110deg) translateY(-3rem);
          opacity: 0.8;
        }
        
        @keyframes float {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        .animate-float {
          animation-name: float;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }
        
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        .animate-heartbeat {
          animation: heartbeat 1.5s ease-in-out infinite;
        }
        
        @keyframes pulse-soft {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1;
        }
        
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .delay-200 {
          animation-delay: 200ms;
        }
        
        .delay-300 {
          animation-delay: 300ms;
        }
      `}</style>
    </>
  );
};

export default AnniversaryGiftPage;
